import * as React from "react";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { LockerCard } from "./LockerCard";
import { LockerTicketDetails } from "./LockerTicketDetails";
import api from "../../utils/api";
import { useRef } from "react";

interface RFIDData {
  bookingDate: string;
  cardAmount: number;
  customerMobileNumber: string;
  customerName: string;
  numberOdAdults: string;
  numberOfChildren: string;
  status: string | null;
  ticketBookingId: number;
  ticketNumber: string;
  ticketRFIdCardRef: string;
  ticketUniqueRFIdNo: string;
}

function LockerAssign() {

  const [lockers, setLockers] = React.useState<
    { lockerId: number; lockerType: "Big Locker" | "Small Locker"; lockerNumber: number; lockerPrice: number; isLocked: boolean; securityCharges: number }[]
  >([]);

  const [rfidData, setRfidData] = React.useState<RFIDData | null>(null);
  const [searchFields, setSearchFields] = React.useState({
    ticketNumber: "",
    customerMobileNumber: "",
    customerName: "",
  });
  const [isSearching, setIsSearching] = React.useState(false);
  const latestSearchRequestRef = React.useRef(0);
  const searchTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastQueryKeyRef = React.useRef<string>("");
  const activeControllerRef = React.useRef<AbortController | null>(null);
  const lastResolvedQueryKeyRef = React.useRef<string>("");
  const manualInputRef = React.useRef<Record<string, boolean>>({
    ticketNumber: false,
    customerMobileNumber: false,
    customerName: false,
  });

  const [selectedLockers, setSelectedLockers] = React.useState<
    { lockerId: number; lockerType: "Big Locker" | "Small Locker"; lockerNumber: number; lockerPrice: number; isLocked: boolean; securityCharges: number }[]
  >([]);

  // card number state
  const [cardNumber, setCardNumber] = React.useState<string>("0");
  // State for locker amount
  const [lockerAmount, setLockerAmount] = React.useState<number>(0);

  // State for locker numbers as comma separated string
  const [lockerNumbers, setLockerNumbers] = React.useState<string>("");

   // State for locker security charges
  const [lockerSecurityCharges, setLockerSecurityCharges] = React.useState<number>(0);

  // State for any one locker id (first if exists)
  const [anyLockerId, setAnyLockerId] = React.useState<number | null>(null);

  // State for total big lockers
  const [totalBigLockers, setTotalBigLockers] = React.useState<number>(0);

  // State for total small lockers
  const [totalSmallLockers, setTotalSmallLockers] = React.useState<number>(0);

  const [totalLockers, setTotalLockers] = React.useState<number>(0);


  React.useEffect(() => {
    const fetchLockers = async () => {
      // Fetch lockers from the API
      try {
        const response = await api.get('/api/LockerMaster/GetAllLocker'); // replace with your API endpoint
        if ((response.status === 200 || response.status === 201) && Array.isArray(response.data)) {
          setLockers(response.data);
        } else {
          setLockers([]);
        }
      } catch (error) {
        console.error("Failed to fetch lockers:", error);
      }
    };
    fetchLockers();
  }, []);

  const handleReadRFID = async (fields: { ticketNumber: string; customerMobileNumber: string; customerName: string; }) => {
    const normalizedFields = {
      ticketNumber: fields.ticketNumber.trim(),
      customerMobileNumber: fields.customerMobileNumber.trim(),
      customerName: fields.customerName.trim(),
    };

    const hasSearchValue =
      normalizedFields.ticketNumber ||
      normalizedFields.customerMobileNumber ||
      normalizedFields.customerName;

    if (!hasSearchValue) {
      setRfidData(null);
      setSearchFields({
        ticketNumber: "",
        customerMobileNumber: "",
        customerName: "",
      });
      setIsSearching(false);
      return;
    }

    const queryKey = JSON.stringify(normalizedFields);
    if (
      lastQueryKeyRef.current === queryKey &&
      activeControllerRef.current
    ) {
      return;
    }

    if (activeControllerRef.current) {
      activeControllerRef.current.abort();
    }

    lastQueryKeyRef.current = queryKey;
    const requestId = ++latestSearchRequestRef.current;
    const controller = new AbortController();
    activeControllerRef.current = controller;
    setIsSearching(true);

    try {
      const query = new URLSearchParams({
        ticketNumber: normalizedFields.ticketNumber,
        customerMobileNumber: normalizedFields.customerMobileNumber,
        customerName: normalizedFields.customerName,
      }).toString();

      const response = await api.get(`/api/RFID/ReadRFID?${query}`, {
        signal: controller.signal,
      });

      if (latestSearchRequestRef.current !== requestId || controller.signal.aborted) {
        return;
      }

      const responsePayload = response.data?.data ?? response.data?.result ?? response.data ?? {};
      const data = Array.isArray(responsePayload) ? responsePayload[0] ?? {} : responsePayload;

      const pickValue = (keys: string[]) => {
        for (const key of keys) {
          if (data?.[key] !== undefined && data?.[key] !== null && data?.[key] !== "") {
            return String(data[key]);
          }
        }
        return "";
      };

      const apiTicketNumber = pickValue([
        "ticketNumber",
        "ticketNo",
        "ticketRFIdCardRef",
        "ticketRFIdRef",
        "ticketRFId",
        "ticketUniqueRFIdNo",
        "cardNumber",
      ]);
      const apiCustomerMobileNumber = pickValue([
        "customerMobileNumber",
        "mobileNumber",
        "contactNumber",
        "phoneNumber",
      ]);
      const apiCustomerName = pickValue([
        "customerName",
        "name",
        "fullName",
      ]);

      const normalizedTicketNumber =
        apiTicketNumber || normalizedFields.ticketNumber;
      const normalizedCustomerMobileNumber =
        apiCustomerMobileNumber || normalizedFields.customerMobileNumber;
      const normalizedCustomerName =
        apiCustomerName || normalizedFields.customerName;

      const filledFields = {
        ticketNumber: normalizedTicketNumber,
        customerMobileNumber: normalizedCustomerMobileNumber,
        customerName: normalizedCustomerName,
      };

      setSearchFields((prev) => {
        const shouldUseApiValues =
          !manualInputRef.current.ticketNumber &&
          !manualInputRef.current.customerMobileNumber &&
          !manualInputRef.current.customerName;

        if (!shouldUseApiValues) {
          return {
            ticketNumber: manualInputRef.current.ticketNumber ? prev.ticketNumber : filledFields.ticketNumber,
            customerMobileNumber: manualInputRef.current.customerMobileNumber
              ? prev.customerMobileNumber
              : filledFields.customerMobileNumber,
            customerName: manualInputRef.current.customerName
              ? prev.customerName
              : filledFields.customerName,
          };
        }

        return {
            ticketNumber:
              filledFields.ticketNumber || prev.ticketNumber,
            customerMobileNumber:
              filledFields.customerMobileNumber || prev.customerMobileNumber,
            customerName:
              filledFields.customerName || prev.customerName
        };
      });

      lastResolvedQueryKeyRef.current = queryKey;

      if (
        !normalizedTicketNumber &&
        !normalizedCustomerMobileNumber &&
        !normalizedCustomerName
      ) {
        setRfidData({
          bookingDate: new Date().toISOString(),
          cardAmount: 0,
          customerMobileNumber: normalizedCustomerMobileNumber,
          customerName: normalizedCustomerName,
          numberOdAdults: "0",
          numberOfChildren: "0",
          status: null,
          ticketBookingId: 0,
          ticketNumber: normalizedTicketNumber,
          ticketRFIdCardRef: normalizedTicketNumber,
          ticketUniqueRFIdNo: normalizedTicketNumber,
        });
        return;
      }
      setRfidData({
        bookingDate: data.bookingDate ?? new Date().toISOString(),
        cardAmount: data.cardAmount ?? 0,
        customerMobileNumber: normalizedCustomerMobileNumber,
        customerName: normalizedCustomerName,
        numberOdAdults: data.numberOdAdults ?? data.numberOfAdults ?? "0",
        numberOfChildren: data.numberOfChildren ?? data.numberOfChildrens ?? "0",
        status: data.status ?? null,
        ticketBookingId: data.ticketBookingId ?? 0,
        ticketNumber: normalizedTicketNumber,
        ticketRFIdCardRef: data.ticketRFIdCardRef || normalizedTicketNumber,
        ticketUniqueRFIdNo: data.ticketUniqueRFIdNo || normalizedTicketNumber,
      });
      if(normalizedFields.customerMobileNumber || normalizedFields.customerName || normalizedFields.ticketNumber){
        setRfidData({
          bookingDate: data.bookingDate ?? new Date().toISOString(),
          cardAmount: data.cardAmount ?? 0,
          customerMobileNumber: normalizedFields.customerMobileNumber || normalizedCustomerMobileNumber,
          customerName: normalizedFields.customerName || normalizedCustomerName,
          numberOdAdults: data.numberOdAdults ?? data.numberOfAdults ?? "0",
          numberOfChildren: data.numberOfChildren ?? data.numberOfChildrens ?? "0",
          status: data.status ?? null,
          ticketBookingId: data.ticketBookingId ?? 0,
          ticketNumber: normalizedFields.ticketNumber || normalizedTicketNumber,
          ticketRFIdCardRef: normalizedFields.ticketNumber || normalizedTicketNumber,
          ticketUniqueRFIdNo: data.ticketUniqueRFIdNo || normalizedTicketNumber,
        });
      }
    } catch (error) {
      if (error instanceof Error && error.name !== "AbortError") {
        setRfidData(null);
      }
    } finally {
      if (latestSearchRequestRef.current === requestId && !controller.signal.aborted) {
        setIsSearching(false);
      }
      if (activeControllerRef.current === controller) {
        activeControllerRef.current = null;
      }
    }
  };

  const handleSearchFieldFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const name = event.target.name as keyof typeof searchFields;
    if (searchFields[name] === "XXXXX") {
      setSearchFields((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSearchFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const nextFields = {
      ...searchFields,
      [name]: value,
    };

    manualInputRef.current = {
      ...manualInputRef.current,
      [name]: true,
    };

    setSearchFields(nextFields);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchTimeoutRef.current = null;
      handleReadRFID(nextFields);
    }, 350);
  };

  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const refs = useRef<(HTMLElement | null)[]>([]);
  const focusNext = (index: number) => {
    refs.current[index + 1]?.focus();
  };

  const focusPrev = (index: number) => {
    refs.current[index - 1]?.focus();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLElement>,
    index: number
  ) => {
    if (e.key === "ArrowRight" || e.key === "Enter") {
      e.preventDefault();
      focusNext(index);
    }

    if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusPrev(index);
    }
  };

  // console.log("Selected Lockers:", selectedLockers);
  // console.log("Locker Amount:", lockerAmount);
  // console.log("Locker Numbers:", lockerNumbers);
  // console.log("Any Locker ID:", anyLockerId);
  // console.log("Total Big Lockers:", totalBigLockers);
  // console.log("Total Small Lockers:", totalSmallLockers);
  // console.log("card number:", cardNumber);
  // console.log("Total Lockers:", totalLockers);
  

  return (
    <div className='page-body'>
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <div className="container mt-4">
                  <header className="bg-white px-4 py-4 fs-5 fw-semibold text-dark border mb-4">
                    <div className="row g-3 mb-3">
                      <div className="col-md-4">
                        <input
                          type="text"
                          className="form-control"
                          name="customerMobileNumber"
                          placeholder="Customer Mobile Number"
                          value={searchFields.customerMobileNumber}
                          onFocus={handleSearchFieldFocus}
                          onChange={handleSearchFieldChange}
                          aria-label="Customer Mobile Number"
                          ref={(el) => void (refs.current[0] = el)}
                          onKeyDown={(e) => handleKeyDown(e, 0)}
                        />
                      </div>
                      <div className="col-md-4">
                        <input
                          type="text"
                          className="form-control"
                          name="customerName"
                          placeholder="Customer Name"
                          value={searchFields.customerName}
                          onFocus={handleSearchFieldFocus}
                          onChange={handleSearchFieldChange}
                          aria-label="Customer Name"
                          ref={(el) => void (refs.current[1] = el)}
                          onKeyDown={(e) => handleKeyDown(e, 1)}
                        />
                      </div>
                      <div className="col-md-4">
                        <input
                          type="text"
                          className="form-control"
                          name="ticketNumber"
                          placeholder="Ticket Number"
                          value={searchFields.ticketNumber}
                          onFocus={handleSearchFieldFocus}
                          onChange={handleSearchFieldChange}
                          aria-label="Ticket Number"
                          ref={(el) => void (refs.current[2] = el)}
                          onKeyDown={(e) => handleKeyDown(e, 2)}
                        />
                      </div>
                    </div>
                    {isSearching && (
                      <div className="text-muted small">Searching RFID data...</div>
                    )}
                  </header>
                  <div className="row gy-4">
                    {/* Product Grid Column */}
                    <div className="col-12 col-md-8">
                      <div className="row gx-3 gy-4">
                        {lockers.length === 0 ? (
                          <div className="col-12 text-center text-muted py-5">
                            No lockers available.
                          </div>
                        ) : (
                          lockers
                            .filter((locker) => !locker.isLocked)
                            .map((locker) => (
                                <div
                                  key={locker.lockerId}
                                  className={`col-12 col-sm-6 col-lg-4`}
                                  style={{
                                  border: selectedLockers.some(l => l.lockerId === locker.lockerId)
                                    ? '2px solid #007bff'
                                    : '2px solid transparent',
                                  borderRadius: 8,
                                  cursor: 'pointer',
                                  }}
                                  onClick={() => {
                                  setSelectedLockers(prev => {
                                    const exists = prev.some(l => l.lockerId === locker.lockerId);
                                    let updated;
                                    if (exists) {
                                    updated = prev.filter(l => l.lockerId !== locker.lockerId);
                                    } else {
                                    updated = [...prev, locker];
                                    }

                                    // Calculate sum of selected locker amounts
                                    const sumAmount = updated.reduce((sum, l) => sum + l.lockerPrice, 0);
                                    setLockerAmount(sumAmount);

                                    // Calculate sum of selected locker security charges
                                    const sumSecurityCharges = updated.reduce((sum, l) => sum + l.securityCharges, 0);
                                    setLockerSecurityCharges(sumSecurityCharges);

                                    // Store locker numbers as comma separated string
                                    const lockerNumbers = updated.map(l => l.lockerNumber).join(',');
                                    setLockerNumbers(lockerNumbers);

                                    // Store any one locker id (first if exists)
                                    setAnyLockerId(updated.length > 0 ? updated[0].lockerId : null);

                                    // Count total big and small lockers
                                    setTotalBigLockers(updated.filter(l => l.lockerType === "Big Locker").length);
                                    setTotalSmallLockers(updated.filter(l => l.lockerType === "Small Locker").length);

                                    setTotalLockers(updated.length);

                                    return updated;
                                  });
                                  }}
                                >
                                  <LockerCard
                                  size={locker.lockerType}
                                  number={locker.lockerNumber}
                                  available={locker.lockerPrice}
                                  used={0}
                                  />
                                </div>
                            ))
                        )}
                      </div>
                    </div>

                    {/* Ticket Details Column */}
                    <div className="col-12 col-md-4">
                      <LockerTicketDetails cardNumber={cardNumber} lockerNumbers={lockerNumbers} lockerAmount={lockerAmount} anyLockerId={anyLockerId} totalBigLockers={totalBigLockers} totalSmallLockers={totalSmallLockers} totalLockers={totalLockers} lockerSecurityCharges={lockerSecurityCharges} rfidData={rfidData} />
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default LockerAssign;
